import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hook";
import {
  userConnection,
  userSignUp,
  userForgetPassword,
  facebookOrGoogleSignIn,
  resetLogin,
  showConnexionModal,
  showSignUp,
  showForgetPassword,
  resetEmail,
  getSignUpData,
  setAuthMode,
  setShowVerificationModal,
} from "../redux/actions";
import type { SignUpData } from "../1_types/signinTypes";
import { MODES } from "../constants";

import {
  USER_CONNECTION_ERROR,
  RESET_PASSWORD_ERROR,
} from "../redux/actions/types";
export const useSignInSignUp = () => {
  const dispatch = useAppDispatch();

  const {
    userSignupError,
    userConnectionError,
    resetPasswordError,
    loadingResetPassword,
    loadingConnexion,
    userConnected,
    email,
    signUpData,
    authMode,
    requireVerification,
    verificationEmail,
  } = useAppSelector((state) => ({
    userSignupError: state.userReducer.userSignupError,
    userConnectionError: state.userReducer.userConnectionError,
    resetPasswordError: state.userReducer.resetPasswordError,
    loadingResetPassword: state.userReducer.loadingResetPassword,
    loadingConnexion: state.userReducer.loadingConnexion,
    userConnected: state.userReducer.userConnected,
    email: state.userReducer.email,
    signUpData: state.userReducer.signUpData,
    authMode: state.userReducer.authMode,
    requireVerification: state.userReducer.requireVerification,
    verificationEmail: state.userReducer.verificationEmail,
  }));

  const {
    signUp,
    showForgetPassword: showForgetPasswordState,
    connexionModal,
    showVerificationModal,
  } = useAppSelector((state) => ({
    signUp: state.componentReducer.signUp,
    showForgetPassword: state.componentReducer.showForgetPassword,
    connexionModal: state.componentReducer.connexionModal,
    showVerificationModal: state.componentReducer.showVerificationModal,
  }));
  const { name, facebook, google, secondaryColor, generalDesign, logoUrl } =
    useAppSelector((state) => ({
      name: state.configurationReducer.name,
      facebook: state.configurationReducer.facebook,
      google: state.configurationReducer.google,
      secondaryColor: state.configurationReducer.secondaryColor,
      generalDesign: state.configurationReducer.generalDesign,
      logoUrl: state.configurationReducer.logoUrl,
    }));

  const handleOpenLogin = useCallback(() => {
    dispatch(setAuthMode(MODES.LOGIN));
    dispatch(resetEmail());
    dispatch(showConnexionModal(true));
    dispatch(showSignUp(false));
  }, [dispatch]);

  const handleOpenSignup = useCallback(() => {
    dispatch(setAuthMode(MODES.SIGNUP));
    dispatch(resetEmail());
    dispatch(showConnexionModal(true));
    dispatch(showSignUp(false));
  }, [dispatch]);

  const handleUserConnection = useCallback(
    (email: string, password: string) => {
      dispatch(userConnection(email, password));
    },
    [dispatch],
  );

  const handleUserSignUp = useCallback(
    (data: SignUpData) => {
      dispatch(
        userSignUp(
          data.email,
          data.password,
          data.nom,
          data.prenom,
          data.telephone,
          data.phoneObj,
          data.newsletter,
        ),
      );
    },
    [dispatch],
  );

  const handleForgetPassword = useCallback(
    (email: string) => {
      dispatch(userForgetPassword(email));
      dispatch({ type: RESET_PASSWORD_ERROR, payload: "" });
    },
    [dispatch],
  );

  const handleFacebookOrGoogleSignIn = useCallback(
    (provider: "facebook" | "google") => {
      dispatch(facebookOrGoogleSignIn(provider));
    },
    [dispatch],
  );

  const handleResetLogin = useCallback(() => {
    dispatch(resetLogin());
  }, [dispatch]);

  const handleShowConnexionModal = useCallback(
    (show: boolean) => {
      dispatch(showConnexionModal(show));
    },
    [dispatch],
  );

  const handleShowSignUp = useCallback(
    (show: boolean) => {
      dispatch(showSignUp(show));
    },
    [dispatch],
  );

  const handleShowForgetPassword = useCallback(
    (show: boolean) => {
      dispatch(showForgetPassword(show));
      dispatch({ type: RESET_PASSWORD_ERROR, payload: "" });
    },
    [dispatch],
  );

  const handleResetEmail = useCallback(() => {
    dispatch(resetEmail());
  }, [dispatch]);

  const handleGetSignUpData = useCallback(
    (data: any) => {
      dispatch(getSignUpData(data));
    },
    [dispatch],
  );

  const handleCloseConnexionModal = useCallback(() => {
    dispatch(showConnexionModal(false));
    dispatch(showSignUp(false));
    dispatch(showForgetPassword(false));
    dispatch(resetEmail());
    dispatch(getSignUpData({}));
    dispatch(setAuthMode(MODES.LOGIN));
  }, [dispatch]);

  const handleUserConnected = useCallback(() => {
    dispatch(showConnexionModal(false));
    dispatch(showSignUp(false));
    dispatch(resetEmail());
    dispatch(getSignUpData({}));
    dispatch(setAuthMode(MODES.LOGIN));
  }, [dispatch]);

  const handleShowVerificationModal = useCallback((show: boolean) => {
    dispatch(setShowVerificationModal(show));
  }, []);

  const handleVerificationSuccess = useCallback(() => {
    dispatch(setShowVerificationModal(false));
    dispatch(showSignUp(true));
  }, [dispatch]);
  const handleGoBack = useCallback(() => {
    dispatch({ type: USER_CONNECTION_ERROR, payload: "" });
    dispatch(showSignUp(false));
    dispatch(resetEmail());
  }, [dispatch]);
  return {
    userSignupError,
    userConnectionError,
    resetPasswordError,
    loadingResetPassword,
    loadingConnexion,
    userConnected,
    email,
    signUpData,
    authMode,
    signUp,
    showForgetPassword: showForgetPasswordState,
    connexionModal,
    name,
    logoUrl,
    facebook,
    google,
    secondaryColor,
    generalDesign,
    requireVerification,
    verificationEmail,
    handleUserConnection,
    handleUserSignUp,
    handleForgetPassword,
    handleFacebookOrGoogleSignIn,
    handleResetLogin,
    handleShowConnexionModal,
    handleShowSignUp,
    handleShowForgetPassword,
    handleResetEmail,
    handleGetSignUpData,
    handleCloseConnexionModal,
    handleUserConnected,
    handleOpenLogin,
    handleOpenSignup,
    showVerificationModal,
    handleShowVerificationModal,
    handleVerificationSuccess,
    handleGoBack,
  };
};
